import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, Facebook, Instagram, Linkedin } from "lucide-react";
import { openAiChat } from "../aichat";
import { Lines, ParallaxPhoto, HeroVideo } from "./motion";
import { QuoteLink } from "./QuotePanel";

/* Rating di legalità AGCM: 2 stelle su 3 con il segno più, nei colori del sito (navy + ciano). */
export function LegalitaBadge() {
  const stella = (piena, key) => (
    <svg key={key} width="34" height="34" viewBox="0 0 24 24" fill={piena ? "#083E7E" : "none"} stroke="#083E7E" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
    </svg>
  );
  return (
    <div
      className="lgbadge"
      role="img"
      aria-label="Rating di legalità: due stelle su tre con il segno più"
      data-testid="legalita-badge"
    >
      {stella(true, "s1")}
      {stella(true, "s2")}
      {stella(false, "s3")}
      <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
        <circle cx="15" cy="15" r="14" fill="#1EA7E8" />
        <path d="M15 8.5v13M8.5 15h13" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function CtaBand({ testid = "cta-band" }) {
  const reduce = useReducedMotion();
  return (
    <section className="ctaband" data-testid={testid}>
      <div className="ctaband-in">
        <motion.h2 initial={reduce ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: reduce ? 0 : 0.4, ease: "easeOut" }}>
          Sopralluogo e preventivo gratuiti. Ti rispondiamo a qualsiasi ora.
        </motion.h2>
        <div className="row">
          <QuoteLink className="btn dark" testid="cta-band-preventivo">
            Richiedi un preventivo
          </QuoteLink>
          <a href="#assistente-ai" className="btn white" onClick={(e) => { e.preventDefault(); openAiChat(); }} data-testid="cta-band-chat">
            <MessageCircle size={18} /> Chat con l'assistente AI
          </a>
        </div>
      </div>
    </section>
  );
}

export function FaqList({ items, testid = "faq" }) {
  return (
    <div className="faqs" data-testid={testid}>
      {items.map(([q, a]) => (
        <details className="faq" key={q} data-testid={`${testid}-item`}>
          <summary>
            <span className="q">{q}</span>
            <span className="pm" aria-hidden="true">
              +
            </span>
          </summary>
          <div className="faq-body">
            <p>{a}</p>
          </div>
        </details>
      ))}
    </div>
  );
}

/* Inner-page hero with parallax photo (or video) and kinetic headline */
export function PageHero({ kick, lines, sub, ph, alt, ctas, trust, video }) {
  const reduce = useReducedMotion();
  return (
    <section className="hero short">
      {video ? <HeroVideo src={video.src} poster={video.poster} /> : <ParallaxPhoto url={ph} />}
      <div className="hero-in">
        {kick && (
          <motion.p className="h-kick" initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.1 }}>
            <span className="dash" />
            {kick}
            <span className="dash" />
          </motion.p>
        )}
        <Lines lines={lines} as="h1" accentLast baseDelay={0.2} />
        {sub && (
          <motion.p className="hero-sub" initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.45, ease: [0.2, 0.7, 0.2, 1] }}>
            {sub}
          </motion.p>
        )}
        {ctas && (
          <motion.div className="hero-ctas" initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.4, delay: reduce ? 0 : 0.6, ease: [0.2, 0.7, 0.2, 1] }} style={{ justifyContent: "center" }}>
            {ctas}
          </motion.div>
        )}
        {trust && <div className="hero-trust">{trust}</div>}
      </div>
    </section>
  );
}

/* Profili social ufficiali (URL forniti dal cliente) */
export function SocialRow({ testid = "social", onLight = false, centered = false }) {
  const SOCIALS = [
    { href: "https://www.facebook.com/orionsecurity.it", label: "Orion Security su Facebook", key: "facebook", Icon: Facebook },
    { href: "https://www.instagram.com/security.it_srl/", label: "Orion Security su Instagram", key: "instagram", Icon: Instagram },
    { href: "https://www.linkedin.com/company/3791542/", label: "Orion Security su LinkedIn", key: "linkedin", Icon: Linkedin },
  ];
  return (
    <div className={`social-row${onLight ? " on-light" : ""}${centered ? " centered" : ""}`} data-testid={testid}>
      {SOCIALS.map(({ href, label, key, Icon }) => (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="social-btn" aria-label={label} data-testid={`${testid}-${key}`}>
          <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
        </a>
      ))}
    </div>
  );
}
